import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { shiftsApi, reportsApi, clientsApi, mentalHealthApi, clockApi, participantApi } from '../services/api';import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { SosButton } from '../components/SosButton';
import { GpsTrackerBadge } from '../components/GpsTrackerBadge';
import { PreShiftHealthCheck } from '../components/PreShiftHealthCheck';
import { useShiftGpsTracker } from '../hooks/useShiftGpsTracker';
import { orgsApi } from '../services/api';
import { toast } from 'sonner';
import {
  FileText, Clock, Users, Heart, Plus,
  ArrowRight, CheckCircle2, TrendingUp,
  ClipboardList, LogOut, Play, Square, UserPlus, Sparkles, ChevronLeft, ChevronRight, GraduationCap, Target, MapPin
} from 'lucide-react';
import { MobileNav } from '../components/MobileNav';
import { LogoHome } from '../components/LogoHome';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function WorkerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [mentalHealthTips, setMentalHealthTips] = useState({ tips: [], resources: [] });
  const [loading, setLoading] = useState(true);

  // Clock state
  const [clockStatus, setClockStatus] = useState({ clocked_in: false, active_record: null });
  const [gpsEnabled, setGpsEnabled] = useState(true);
  const [preShiftReady, setPreShiftReady] = useState(false);
  const [preShiftPosition, setPreShiftPosition] = useState(null);
  const gpsActive = clockStatus.clocked_in && gpsEnabled;
  const { totalKm: gpsTotalKm, status: gpsStatus, error: gpsError, lastPingTs: gpsLastPing } = useShiftGpsTracker(gpsActive);
  const [clockLoading, setClockLoading] = useState(false);
  const [elapsed, setElapsed] = useState('00:00:00');
  const [selectedClient, setSelectedClient] = useState('');
  // Thank-you Wall
  const [celebrations, setCelebrations] = useState([]);
  const [celebrationIndex, setCelebrationIndex] = useState(0);
  // Clock-in goals dialog
  const [clockInDialogOpen, setClockInDialogOpen] = useState(false);
  const [selectedClockClient, setSelectedClockClient] = useState('');
  const [shiftGoals, setShiftGoals] = useState([]);
  const [selectedShiftGoalIds, setSelectedShiftGoalIds] = useState([]);
  const [loadingShiftGoals, setLoadingShiftGoals] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Live timer
  useEffect(() => {
    if (!clockStatus.clocked_in || !clockStatus.active_record) return;
    const interval = setInterval(() => {
      const start = new Date(clockStatus.active_record.clock_in);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      const h = String(Math.floor(diff / 3600)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const s = String(diff % 60).padStart(2, '0');
      setElapsed(`${h}:${m}:${s}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [clockStatus]);

  const loadDashboardData = async () => {
    try {
      const [shiftsRes, reportsRes, clientsRes, tipsRes, clockRes, celebsRes, orgRes] = await Promise.all([
        shiftsApi.getAll(),
        reportsApi.getAll(),
        clientsApi.getAll(),
        mentalHealthApi.getTips(),
        clockApi.getStatus(),
        participantApi.getWorkerCelebrations().catch(() => ({ data: { total: 0, celebrations: [] } })),
        orgsApi.getMine().catch(() => ({ data: { gps_tracking_enabled: true } })),
      ]);
      setShifts(shiftsRes.data);
      setRecentReports(reportsRes.data.slice(0, 5));
      setClients(clientsRes.data);
      setMentalHealthTips(tipsRes.data);
      setClockStatus(clockRes.data);
      setCelebrations(celebsRes.data.celebrations || []);
      setGpsEnabled(orgRes.data?.gps_tracking_enabled !== false);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    // Open dialog so worker can choose client + goals to focus on
    setClockInDialogOpen(true);
    setSelectedClockClient('');
    setShiftGoals([]);
    setSelectedShiftGoalIds([]);
  };

  const loadShiftGoalsForClient = async (clientId) => {
    setSelectedClockClient(clientId);
    setSelectedShiftGoalIds([]);
    if (!clientId) { setShiftGoals([]); return; }
    setLoadingShiftGoals(true);
    try {
      const res = await clockApi.getClientGoals(clientId);
      setShiftGoals(res.data || []);
    } catch {
      setShiftGoals([]);
    } finally {
      setLoadingShiftGoals(false);
    }
  };

  const submitClockIn = async () => {
    setClockLoading(true);

    // PreShiftHealthCheck has already validated location and cached the first position.
    const firstPosition = gpsEnabled ? preShiftPosition : null;

    try {
      const res = await clockApi.clockIn({
        client_id: selectedClockClient || null,
        selected_goal_ids: selectedShiftGoalIds,
      });
      setClockStatus({ clocked_in: true, active_record: res.data });

      // Fire-and-forget first GPS ping so admin dashboard sees the worker's
      // location immediately rather than waiting for the next watch-flush.
      if (firstPosition) {
        clockApi.gpsPing([{
          lat: firstPosition.coords.latitude,
          lng: firstPosition.coords.longitude,
          accuracy_m: firstPosition.coords.accuracy,
          ts: new Date(firstPosition.timestamp || Date.now()).toISOString(),
        }]).catch(() => {/* silent — watcher will retry */});
      }

      const goalCount = selectedShiftGoalIds.length;
      const locMsg = firstPosition ? ' · 📍 Location shared' : '';
      toast.success(
        (goalCount > 0
          ? `Clocked in — focusing on ${goalCount} goal${goalCount !== 1 ? 's' : ''} this shift 💪`
          : 'Clocked in! Your shift has started.') + locMsg
      );
      setClockInDialogOpen(false);
      // Reset for next time the dialog opens
      setPreShiftReady(false);
      setPreShiftPosition(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    setClockLoading(true);
    try {
      const res = await clockApi.clockOut();
      setClockStatus({ clocked_in: false, active_record: null });
      const hrs = res.data.total_hours;
      const km = res.data.total_km;
      const kmMsg = (typeof km === 'number' && km > 0) ? ` · ${km.toFixed(2)} km tracked` : '';
      toast.success(`Clocked out! Shift duration: ${Math.floor(hrs)}h ${Math.round((hrs % 1) * 60)}m${kmMsg}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockLoading(false);
    }
  };

  const handleAddClientTime = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }
    try {
      const res = await clockApi.addClientTime(clockStatus.active_record.id, selectedClient);
      setClockStatus({ clocked_in: true, active_record: res.data });
      const clientName = clients.find(c => c.id === selectedClient)?.name || 'Client';
      toast.success(`Started with ${clientName}`);
      setSelectedClient('');
    } catch (error) {
      toast.error('Failed to log client start time');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const pendingShifts = shifts.filter(s => s.status === 'pending');
  const completedReports = recentReports.filter(r => r.status === 'submitted').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden" data-testid="worker-dashboard">
      {/* Decorative animated gradient blobs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-teal-400/40 blur-3xl animate-pulse-slow" />
        <div className="absolute top-1/3 -left-20 h-80 w-80 rounded-full bg-emerald-500/30 blur-3xl animate-blob-float" />
        <div className="absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-[30rem] w-[30rem] rounded-full bg-teal-300/35 blur-3xl animate-pulse-slow" style={{ animationDelay: '3s' }} />
        <div className="absolute top-10 right-1/4 h-64 w-64 rounded-full bg-cyan-300/30 blur-3xl animate-blob-float" style={{ animationDelay: '5s' }} />
        <div className="absolute top-1/2 right-1/3 h-48 w-48 rounded-full bg-amber-200/30 blur-3xl" />
      </div>

      {/* Floating sparkles & geometric accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden hidden lg:block">
        <svg className="absolute top-32 right-32 h-6 w-6 opacity-60 animate-twinkle" viewBox="0 0 24 24" fill="rgb(20 184 166)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute bottom-1/3 right-12 h-4 w-4 opacity-70 animate-twinkle" style={{ animationDelay: '1.5s' }} viewBox="0 0 24 24" fill="rgb(16 185 129)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute top-1/2 left-1/3 h-5 w-5 opacity-55 animate-twinkle" style={{ animationDelay: '0.8s' }} viewBox="0 0 24 24" fill="rgb(6 182 212)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute bottom-32 right-1/3 h-4 w-4 opacity-60 animate-twinkle" style={{ animationDelay: '2.2s' }} viewBox="0 0 24 24" fill="rgb(245 158 11)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <div className="absolute top-[18%] right-[12%] h-3 w-3 rounded-full bg-teal-500/80 animate-float-1" />
        <div className="absolute top-[55%] right-[8%] h-3 w-3 rounded-full bg-emerald-500/75 animate-float-2" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-[18%] right-[40%] h-2.5 w-2.5 rotate-45 bg-cyan-500/70 animate-float-3" style={{ animationDelay: '1s' }} />
        <div className="absolute top-[35%] left-[8%] h-2.5 w-2.5 rounded-full bg-amber-500/70 animate-float-1" style={{ animationDelay: '3s' }} />
        <svg className="absolute top-1/4 right-1/3 h-24 w-24 opacity-25 animate-spin-slow" viewBox="0 0 100 100" fill="none">
          <polygon points="50,10 85,30 85,70 50,90 15,70 15,30" stroke="rgb(20 184 166)" strokeWidth="1" fill="none" />
          <polygon points="50,25 70,37 70,63 50,75 30,63 30,37" stroke="rgb(45 212 191)" strokeWidth="0.7" fill="rgb(20 184 166 / 0.1)" />
        </svg>
        <svg className="absolute bottom-12 left-1/3 h-28 w-28 opacity-20 animate-spin-slow" style={{ animationDirection: 'reverse' }} viewBox="0 0 200 200" fill="none">
          <circle cx="100" cy="100" r="80" stroke="rgb(20 184 166)" strokeWidth="0.7" />
          <circle cx="100" cy="100" r="60" stroke="rgb(20 184 166)" strokeWidth="0.7" strokeDasharray="2 4" />
          <circle cx="100" cy="100" r="40" stroke="rgb(45 212 191)" strokeWidth="0.7" />
        </svg>
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <LogoHome className="h-14 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600 hidden sm:block">
                Welcome, <span className="font-medium text-slate-900">{user?.name}</span>
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Motivational Quote */}
        <div className="mb-4 flex items-start gap-2.5" data-testid="motivational-quote">
          <span className="text-3xl text-teal-500 leading-none select-none" aria-hidden="true">&ldquo;</span>
          <p className="font-heading italic text-base sm:text-lg text-transparent bg-clip-text bg-gradient-to-r from-teal-600 via-emerald-600 to-cyan-600 leading-snug">
            Every shift is a chance to make someone's world safer.
          </p>
        </div>

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600">Here's your overview for today</p>
        </div>

        {/* Clock In/Out Widget */}
        <Card className={`mb-8 border-2 transition-colors ${clockStatus.clocked_in ? 'border-green-400 bg-green-50/50' : 'border-slate-200'}`} data-testid="clock-widget">
          <CardContent className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Clock status & timer */}
              <div className="flex items-center gap-4 flex-1">
                <div className={`p-3 rounded-xl ${clockStatus.clocked_in ? 'bg-green-100' : 'bg-slate-100'}`}>
                  <Clock className={`h-7 w-7 ${clockStatus.clocked_in ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-slate-900">
                      {clockStatus.clocked_in ? 'On Shift' : 'Off Shift'}
                    </h3>
                    {clockStatus.clocked_in && (
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                      </span>
                    )}
                  </div>
                  {clockStatus.clocked_in ? (
                    <p className="text-sm text-slate-600">
                      Started {new Date(clockStatus.active_record?.clock_in).toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' })}
                      {gpsEnabled && (
                        <span className="ml-2 text-emerald-700 font-medium" data-testid="clock-km-inline">
                          · {(gpsTotalKm || clockStatus.active_record?.total_km || 0).toFixed(2)} km
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500">Clock in to start your shift</p>
                  )}
                </div>
              </div>

              {/* Timer display */}
              {clockStatus.clocked_in && (
                <div className="font-mono text-3xl font-bold text-green-700 tracking-wider tabular-nums" data-testid="clock-timer">
                  {elapsed}
                </div>
              )}

              {/* Clock button */}
              {clockStatus.clocked_in ? (
                <Button
                  onClick={handleClockOut}
                  disabled={clockLoading}
                  className="bg-red-600 hover:bg-red-700 text-white h-12 px-6"
                  data-testid="clock-out-btn"
                >
                  <Square className="h-4 w-4 mr-2" />
                  {clockLoading ? 'Clocking Out...' : 'Clock Out'}
                </Button>
              ) : (
                <Button
                  onClick={handleClockIn}
                  disabled={clockLoading}
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6"
                  data-testid="clock-in-btn"
                >
                  <Play className="h-4 w-4 mr-2" />
                  {clockLoading ? 'Clocking In...' : 'Clock In'}
                </Button>
              )}
            </div>

            {/* Client time logging - only visible when clocked in */}
            {clockStatus.clocked_in && (
              <div className="mt-4 pt-4 border-t border-green-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <p className="text-sm font-medium text-slate-700">Log client start time:</p>
                  <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                      <SelectTrigger className="h-10 flex-1 bg-white" data-testid="client-time-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500" data-testid="client-time-empty">No participants added yet</div>
                        )}
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleAddClientTime}
                      className="h-10 border-green-300 text-green-700 hover:bg-green-50"
                      data-testid="add-client-time-btn"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Start
                    </Button>
                  </div>
                </div>

                {/* Client times logged */}
                {clockStatus.active_record?.client_times?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {clockStatus.active_record.client_times.map((ct, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-600 bg-white/80 rounded px-3 py-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                        <span className="font-medium">{ct.client_name}</span>
                        <span className="text-slate-400">-</span>
                        <span>{new Date(ct.start_time).toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="card-hover">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Shifts</p>
                  <p className="text-2xl font-bold text-slate-900">{pendingShifts.length}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Reports Submitted</p>
                  <p className="text-2xl font-bold text-slate-900">{completedReports}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Participants</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="worker-client-count">{clients.length}</p>
                  {clients.length === 0 && (
                    <p className="text-[11px] text-amber-700 mt-1 leading-snug" data-testid="worker-no-clients-hint">
                      No participants in your organisation yet — ask your admin to add them under Clients.
                    </p>
                  )}
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Reports</p>
                  <p className="text-2xl font-bold text-slate-900">{recentReports.length}</p>
                </div>
                <div className="p-3 bg-teal-100 rounded-lg">
                  <FileText className="h-6 w-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Thank-You Wall */}
        {celebrations.length > 0 && (
          <Card className="mb-8 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white overflow-hidden" data-testid="thankyou-wall">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Sparkles className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-slate-900 text-base sm:text-lg">Thank-You Wall</CardTitle>
                    <CardDescription className="text-slate-500 text-xs">
                      {celebrations.length} celebration{celebrations.length !== 1 ? 's' : ''} received from participants & families 💚
                    </CardDescription>
                  </div>
                </div>
                {celebrations.length > 1 && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-slate-500 hover:text-emerald-600"
                      onClick={() => setCelebrationIndex(i => (i - 1 + celebrations.length) % celebrations.length)}
                      data-testid="celebration-prev-btn"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-slate-500 tabular-nums">
                      {celebrationIndex + 1} / {celebrations.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-slate-500 hover:text-emerald-600"
                      onClick={() => setCelebrationIndex(i => (i + 1) % celebrations.length)}
                      data-testid="celebration-next-btn"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {(() => {
                const c = celebrations[celebrationIndex] || celebrations[0];
                return (
                  <div className="bg-white border border-emerald-100 rounded-xl p-4 sm:p-5" data-testid="current-celebration">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold shrink-0">
                        {(c.participant_name || '?').split(' ').map(n => n[0]).slice(0, 2).join('')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-slate-900">{c.participant_name}</p>
                          {c.client_name && c.client_name !== c.participant_name && (
                            <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-300">
                              for {c.client_name}
                            </Badge>
                          )}
                          <span className="text-xs text-slate-400">·</span>
                          <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 mt-1.5">
                          <Sparkles className="h-3 w-3 mr-1" />
                          {c.category_name}
                        </Badge>
                        <blockquote className="mt-3 text-slate-700 italic leading-relaxed border-l-2 border-emerald-300 pl-3 text-sm sm:text-base">
                          "{c.message}"
                        </blockquote>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {celebrations.length > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-3">
                  {celebrations.slice(0, 10).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCelebrationIndex(i)}
                      className={`h-1.5 rounded-full transition-all ${i === celebrationIndex ? 'w-6 bg-emerald-500' : 'w-1.5 bg-emerald-200 hover:bg-emerald-300'}`}
                      aria-label={`View celebration ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link to="/reports/new" data-testid="new-report-btn">
            <Card className="card-hover cursor-pointer group h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-4 bg-teal-600 rounded-xl group-hover:bg-teal-700 transition-colors">
                  <Plus className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">New Report</h3>
                  <p className="text-sm text-slate-500">Write a shift report</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 ml-auto group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/handover" data-testid="handover-btn">
            <Card className="card-hover cursor-pointer group h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-4 bg-slate-900 rounded-xl group-hover:bg-slate-800 transition-colors">
                  <ClipboardList className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">Shift Handover</h3>
                  <p className="text-sm text-slate-500">Create handover notes</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 ml-auto group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/wellbeing" data-testid="wellbeing-btn">
            <Card className="card-hover cursor-pointer group h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-4 bg-pink-500 rounded-xl group-hover:bg-pink-600 transition-colors">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">Wellbeing Hub</h3>
                  <p className="text-sm text-slate-500">Mental health check-in</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 ml-auto group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/team-chat" data-testid="team-chat-btn">
            <Card className="card-hover cursor-pointer group h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-4 bg-blue-600 rounded-xl group-hover:bg-blue-700 transition-colors">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">Team Chat</h3>
                  <p className="text-sm text-slate-500">Message your team</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 ml-auto group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/roster" data-testid="roster-btn">
            <Card className="card-hover cursor-pointer group h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-4 bg-amber-500 rounded-xl group-hover:bg-amber-600 transition-colors">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">My Roster</h3>
                  <p className="text-sm text-slate-500">Shifts & availability</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 ml-auto group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/training" data-testid="training-btn">
            <Card className="card-hover cursor-pointer group h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-4 bg-purple-600 rounded-xl group-hover:bg-purple-700 transition-colors">
                  <GraduationCap className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">Training</h3>
                  <p className="text-sm text-slate-500">NDIS micro-modules</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 ml-auto group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Reports */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Recent Reports</CardTitle>
                  <CardDescription>Your latest documentation</CardDescription>
                </div>
                <Link to="/reports">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </CardHeader>
              <CardContent>
                {recentReports.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No reports yet. Create your first report!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentReports.map((report) => (
                      <Link 
                        to={`/reports/${report.id}`} 
                        key={report.id}
                        className="block p-4 rounded-lg border border-slate-200 hover:border-teal-500/50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900 capitalize">
                            {report.template_type.replace(/_/g, ' ')}
                          </span>
                          <Badge 
                            variant={report.status === 'submitted' ? 'default' : 'secondary'}
                            className={report.status === 'submitted' ? 'bg-green-100 text-green-700' : 
                              report.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                              report.status === 'changes_requested' ? 'bg-amber-100 text-amber-700' : ''}
                          >
                            {report.status === 'changes_requested' ? 'Changes Requested' : report.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">
                          {new Date(report.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        {report.compliance_score && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-500">Compliance</span>
                              <span className="font-medium">{report.compliance_score}%</span>
                            </div>
                            <Progress value={report.compliance_score} className="h-1.5" />
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div>
            {/* Wellbeing Tips */}
            <Card className="bg-gradient-to-br from-teal-50 to-white border-teal-100">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Heart className="h-5 w-5 text-teal-600" />
                  Wellbeing Tip
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mentalHealthTips.tips.length > 0 && (
                  <div className="space-y-3">
                    <div className="p-4 bg-white rounded-lg border border-teal-100">
                      <h4 className="font-medium text-slate-900 mb-1">
                        {mentalHealthTips.tips[Math.floor(Math.random() * mentalHealthTips.tips.length)]?.title}
                      </h4>
                      <p className="text-sm text-slate-600">
                        {mentalHealthTips.tips[Math.floor(Math.random() * mentalHealthTips.tips.length)]?.content}
                      </p>
                    </div>
                    <Link to="/wellbeing">
                      <Button variant="outline" className="w-full border-teal-200 text-teal-700 hover:bg-teal-50">
                        Check In Now
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="font-heading text-base">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/compliance" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <TrendingUp className="h-5 w-5 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">Compliance Metrics</span>
                </Link>
                <Link to="/clients" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <Users className="h-5 w-5 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">View Clients</span>
                </Link>
                <Link to="/templates" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <FileText className="h-5 w-5 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">Report Templates</span>
                </Link>
                <Link to="/saved-pdfs" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <FileText className="h-5 w-5 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">Saved PDFs</span>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Clock-In with Shift Goals Dialog */}
      <Dialog open={clockInDialogOpen} onOpenChange={setClockInDialogOpen}>        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-emerald-600" />
              Start your shift
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-900 flex gap-2">
              <Target className="h-4 w-4 mt-0.5 shrink-0" />
              <p>Choose the participant you're working with today, then tick the goals you plan to focus on. They'll be carried into your shift report.</p>
            </div>

            <div>
              <Label className="text-sm">Participant for this shift</Label>
              <Select value={selectedClockClient} onValueChange={loadShiftGoalsForClient}>
                <SelectTrigger className="mt-1" data-testid="clockin-client-select">
                  <SelectValue placeholder="Choose participant (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {clients.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No participants added yet</div>}
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {selectedClockClient && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Goals to work toward</Label>
                  {shiftGoals.length > 0 && (
                    <span className="text-xs text-slate-500">{selectedShiftGoalIds.length}/{shiftGoals.length} selected</span>
                  )}
                </div>
                {loadingShiftGoals ? (
                  <div className="text-center py-4 text-sm text-slate-400">Loading goals...</div>
                ) : shiftGoals.length === 0 ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-500 text-center">
                    No active goals for this participant.
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="shift-goals-list">
                    {shiftGoals.map(g => {
                      const checked = selectedShiftGoalIds.includes(g.id);
                      return (
                        <label
                          key={g.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-teal-50 border-teal-300' : 'bg-white border-slate-200 hover:border-teal-200'}`}
                          data-testid={`shift-goal-${g.id}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              if (v) setSelectedShiftGoalIds([...selectedShiftGoalIds, g.id]);
                              else setSelectedShiftGoalIds(selectedShiftGoalIds.filter(id => id !== g.id));
                            }}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900">{g.title}</p>
                            {g.description && <p className="text-xs text-slate-500 mt-0.5">{g.description}</p>}
                            <div className="flex items-center gap-2 mt-1">
                              {g.category && <span className="text-[10px] capitalize bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{g.category.replace('_', ' ')}</span>}
                              {typeof g.progress === 'number' && (
                                <span className="text-[10px] text-teal-600">{g.progress}% done</span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {gpsEnabled && (
              <PreShiftHealthCheck
                enabled={gpsEnabled}
                onReady={(ready, pos) => { setPreShiftReady(ready); setPreShiftPosition(pos); }}
              />
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setClockInDialogOpen(false)} disabled={clockLoading}>
                Cancel
              </Button>
              <Button
                onClick={submitClockIn}
                disabled={clockLoading || (gpsEnabled && !preShiftReady)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-clock-in-btn"
                title={gpsEnabled && !preShiftReady ? 'Please grant location permission to start the shift' : ''}
              >
                <Play className="h-4 w-4 mr-1" />
                {clockLoading
                  ? 'Starting...'
                  : (gpsEnabled && !preShiftReady)
                    ? 'Waiting for location…'
                    : selectedShiftGoalIds.length > 0
                      ? `Start shift with ${selectedShiftGoalIds.length} goal${selectedShiftGoalIds.length !== 1 ? 's' : ''}`
                      : 'Start shift'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Emergency SOS — floating button */}
      <SosButton clientId={clockStatus?.active_record?.client_id} />
      <GpsTrackerBadge totalKm={gpsTotalKm} status={gpsStatus} error={gpsError} lastPingTs={gpsLastPing} />
    </div>
  );
}
